import { clamp, coverPlacement, countedHero, composerFonts, normaliseComposerDesign } from './share-composer-model';
import { drawProfileQR } from './profile-qr';

function round(ctx, x, y, w, h, radius = 18) {
  const r = Math.min(radius, w / 2, h / 2);
  ctx.beginPath(); ctx.moveTo(x + r, y); ctx.arcTo(x + w, y, x + w, y + h, r); ctx.arcTo(x + w, y + h, x, y + h, r); ctx.arcTo(x, y + h, x, y, r); ctx.arcTo(x, y, x + w, y, r); ctx.closePath();
}
function text(ctx, value, x, y, size, color, font, weight = 500, width = Infinity) {
  ctx.font = `${weight} ${size}px ${font}`; ctx.fillStyle = color;
  let label = String(value || '');
  if (ctx.measureText(label).width > width) {
    while (label.length && ctx.measureText(`${label}…`).width > width) label = label.slice(0, -1);
    label += '…';
  }
  ctx.fillText(label, x, y);
}
// A small halo sits behind the sharp glyphs; only the hero figure uses it.
function heroText(ctx, value, x, y, size, color, font, width, light, halo = color, scale = 1) {
  ctx.save();
  // Blur only the halo pass, then paint untouched glyphs on top. Scale the
  // canvas shadow explicitly so preview, HD GIF and 4K photo look consistent.
  ctx.shadowColor = `${halo}${light ? '24' : '66'}`;
  ctx.shadowBlur = Math.min(22, size * .18) * scale;
  text(ctx, value, x, y, size, color, font, 700, width);
  ctx.shadowBlur = 0;
  text(ctx, value, x, y, size, color, font, 700, width);
  ctx.restore();
}
// Captions are authored for the shared card, never copied from private notes.
function captionLines(ctx, value, width) {
  const lines = [];
  for (const paragraph of value.split('\n')) {
    let line = '';
    for (const word of paragraph.split(/\s+/).filter(Boolean)) {
      if (line && ctx.measureText(line + word).width > width) { lines.push(line.trimEnd()); line = ''; }
      for (const char of word) {
        if (ctx.measureText(line + char).width > width && line) { lines.push(line.trimEnd()); line = ''; }
        line += char;
      }
      line += ' ';
    }
    lines.push(line.trimEnd());
  }
  return lines;
}
function drawCaption(ctx, model, design, theme, ink, w, h, pad, story, scale, layout) {
  if (!model.caption) return;
  const width = w - pad * 2 - 32, font = theme.caption || composerFonts(design, theme).caption;
  let size = design.captionSize, lines;
  const value = model.caption.split('\n').length > 3 ? model.caption.replace(/\s+/g,' ') : model.caption;
  const maxHeight = h < 900 ? 70 : layout === 'chart' ? 90 : 210;
  do { ctx.font = `600 ${size}px ${font}`; lines = captionLines(ctx, value, width); if ((lines.length <= 3 && lines.length * size * 1.25 <= maxHeight) || size <= 18) break; size--; } while (true);
  const lineHeight = size * 1.25, footer = h - (story ? 260 : 58), top = pad + (story ? 130 : 0) + size;
  const lastY = Math.max(top + (lines.length - 1) * lineHeight, footer - (h < 900 ? 66 : 86));
  const y = top + (lastY - top - (lines.length - 1) * lineHeight) * design.captionY / 100;
  const align = design.captionAlign, x = align === 'center' ? w / 2 : align === 'right' ? w - pad - 16 : pad + 16;
  const actualWidth = Math.max(...lines.map(line => ctx.measureText(line).width));
  const left = align === 'center' ? x - actualWidth / 2 : align === 'right' ? x - actualWidth : x;
  ctx.save();
  if (design.captionPlate) { round(ctx, left - 16, y - size - 12, actualWidth + 32, size + (lines.length - 1) * lineHeight + 24, 14); ctx.fillStyle = '#00000088'; ctx.fill(); }
  ctx.textAlign = align; ctx.shadowColor = '#00000099'; ctx.shadowBlur = 5 * scale;
  lines.forEach((line, index) => text(ctx, line, x, y + index * lineHeight, size, design.captionColour || ink, font, 600));
  ctx.restore();
}
function wordmark(ctx, x, y, size, light) {
  ctx.font = `700 ${size}px "Barlow Condensed", sans-serif`;
  for (const [word, color] of [['TRADE', light ? '#080909' : '#ffffff'], ['LOG', '#4ade80']]) {
    ctx.fillStyle = color;
    for (const letter of word) { ctx.fillText(letter, x, y); x += ctx.measureText(letter).width + size * .1; }
  }
}
function procedural(ctx, w, h, theme, type, time) {
  ctx.fillStyle = theme.bg; ctx.fillRect(0, 0, w, h);
  if (type === 'plain') return;
  const t = time * Math.PI / 3, alpha = theme.light ? '26' : '55';
  if (type === 'aurora') {
    for (let i = 0; i < 3; i++) {
      const x = w * (.25 + .25 * i) + Math.sin(t + i * 2) * w * .16;
      const y = h * (.3 + .17 * i) + Math.cos(t + i) * h * .18;
      const glow = ctx.createRadialGradient(x, y, 0, x, y, w * .55);
      glow.addColorStop(0, `${i === 1 ? theme.positive : theme.accent}${alpha}`); glow.addColorStop(1, `${theme.accent}00`);
      ctx.fillStyle = glow; ctx.fillRect(0, 0, w, h);
    }
  }
  ctx.save(); ctx.strokeStyle = `${theme.accent}${theme.light ? '22' : '30'}`; ctx.lineWidth = 1;
  if (type === 'market') {
    for (let x = 0; x < w; x += 70) { ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, h); ctx.stroke(); }
    for (let y = 0; y < h; y += 70) { ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(w, y); ctx.stroke(); }
  }
  const lines = type === 'silk' ? 20 : type === 'market' ? 10 : 6;
  for (let i = 0; i < lines; i++) {
    ctx.beginPath();
    for (let x = 0; x <= w + 12; x += 12) {
      const y = h * .6 + i * 14 + Math.sin(x / w * Math.PI * 2 + t + i * .08) * h * .16 + Math.cos(x / w * 4 - t * .5) * h * .08;
      x === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
    }
    ctx.globalAlpha = type === 'silk' ? .6 : .45; ctx.stroke();
  }
  ctx.restore();
}
function containedImage(ctx, image, x, y, width, height) {
  const iw = image.naturalWidth || image.videoWidth || image.width, ih = image.naturalHeight || image.videoHeight || image.height;
  if (!iw || !ih) return;
  const scale = Math.min(width / iw, height / ih);
  ctx.drawImage(image, x + (width - iw * scale) / 2, y + (height - ih * scale) / 2, iw * scale, ih * scale);
}
function equity(ctx, values, x, y, width, height, color, theme) {
  if (values.length < 2) return;
  const low = Math.min(0, ...values), high = Math.max(0, ...values), range = high - low || 1;
  const points = values.map((value, index) => [x + index / (values.length - 1) * width, y + height - (value - low) / range * height]);
  const path = () => { ctx.beginPath(); points.forEach(([px, py], index) => index === 0 ? ctx.moveTo(px, py) : ctx.lineTo(px, py)); };
  const gradient = ctx.createLinearGradient(0, y, 0, y + height); gradient.addColorStop(0, `${color}35`); gradient.addColorStop(1, `${color}00`);
  path(); ctx.lineTo(x + width, y + height); ctx.lineTo(x, y + height); ctx.closePath(); ctx.fillStyle = gradient; ctx.fill();
  ctx.strokeStyle = `${theme.t1}20`; ctx.lineWidth = 1; ctx.beginPath(); ctx.moveTo(x, y + height); ctx.lineTo(x + width, y + height); ctx.stroke();
  path(); ctx.strokeStyle = color; ctx.lineWidth = 3; ctx.lineJoin = 'round'; ctx.lineCap = 'round'; ctx.stroke();
  const last = points.at(-1); ctx.beginPath(); ctx.arc(last[0], last[1], 5, 0, Math.PI * 2); ctx.fillStyle = color; ctx.fill();
}

function monthlyBars(ctx, model, x, y, width, height, theme, time, animated) {
  text(ctx, 'MONTHLY NET RETURN', x, y + 18, 17, theme.ink, theme.font, 600);
  text(ctx, model.unit === 'currency' ? 'Estimated P&L · selected account base' : 'Recorded return (%) · not compounded', x, y + 43, 13, theme.muted, theme.font, 400, width);
  const values = model.months.map(month => month.value), high = Math.max(1, ...values), low = Math.min(0, ...values);
  const top = y + 78, graphH = height - 148, range = high - low, zero = top + high / range * graphH;
  ctx.lineWidth = 1; ctx.strokeStyle = `${theme.ink}15`;
  for (let i = 0; i < 4; i++) { const gy = top + i / 3 * graphH; ctx.beginPath(); ctx.moveTo(x, gy); ctx.lineTo(x + width, gy); ctx.stroke(); }
  ctx.strokeStyle = `${theme.ink}55`; ctx.beginPath(); ctx.moveTo(x, zero); ctx.lineTo(x + width, zero); ctx.stroke();
  const slot = width / 12, barWidth = slot * .48;
  model.months.forEach((month, index) => {
    const bx = x + index * slot + slot / 2, fraction = animated ? 1 - Math.pow(1 - clamp((time - index * .045) / .95, 0, 1), 3) : 1;
    const barH = Math.abs(month.value) / range * graphH * fraction, color = month.value < 0 ? theme.negative : theme.positive;
    if (barH > .2) {
      const by = month.value < 0 ? zero : zero - barH;
      const fill = ctx.createLinearGradient(bx, by, bx, by + barH); fill.addColorStop(0, color); fill.addColorStop(1, `${color}88`);
      round(ctx, bx - barWidth / 2, by, barWidth, Math.max(1, barH), Math.min(6, barH / 2)); ctx.fillStyle = fill; ctx.fill();
    } else if (month.reported) { ctx.fillStyle = theme.muted; round(ctx, bx - barWidth / 2, zero - 1, barWidth, 2, 1); ctx.fill(); }
    ctx.save(); ctx.textAlign = 'center';
    text(ctx, month.label.toUpperCase(), bx, top + graphH + 28, 13, theme.muted, theme.font, 500);
    // Each signed return sits above its bar, inside its own month slot.
    let labelSize = 15, label = month.display; ctx.font = `600 ${labelSize}px ${theme.heading}`;
    if (ctx.measureText(label).width > slot - 4 && model.unit === 'currency' && month.reported) label = month.compact;
    while (labelSize > 10 && ctx.measureText(label).width > slot - 4) { labelSize--; ctx.font = `600 ${labelSize}px ${theme.heading}`; }
    text(ctx, label, bx, (month.value > 0 ? zero - barH : zero) - 13, labelSize, month.reported ? color : theme.muted, theme.heading, 600);
    ctx.restore();
  });
  text(ctx, '—  No recorded return', x, y + height, 12, theme.muted, theme.font, 400);
}

function premiumFrame(ctx, w, h, pad, theme, time) {
  ctx.save();
  const sheen = ctx.createLinearGradient(0, h, w, 0);
  sheen.addColorStop(0, `${theme.accent}05`); sheen.addColorStop(.35, `${theme.accent}20`); sheen.addColorStop(.5, `${theme.accent}66`); sheen.addColorStop(.65, `${theme.accent}18`); sheen.addColorStop(1, `${theme.accent}05`);
  round(ctx, 20, 20, w - 40, h - 40, 24); ctx.strokeStyle = sheen; ctx.lineWidth = 1.5; ctx.stroke();
  // A few vector contours create a shifting foil surface without image assets
  // or per-pixel effects. The same paths are rendered into every export.
  ctx.strokeStyle = `${theme.accent}22`; ctx.lineWidth = 1;
  for (let i = 0; i < 9; i++) {
    ctx.beginPath();
    for (let p = 0; p <= 64; p++) {
      const angle = p / 64 * Math.PI * 2, radius = 145 + i * 16;
      const x = w * .81 + Math.cos(angle) * radius, y = h * .4 + Math.sin(angle) * radius * .6 + Math.sin(time * .5 + angle * 2 + i * .18) * 18;
      p === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
    }
    ctx.closePath(); ctx.stroke();
  }
  const bandX = (Math.sin(time * .3) * .3 + .65) * w;
  const band = ctx.createLinearGradient(bandX - 160, 0, bandX + 160, h);
  band.addColorStop(0, `${theme.accent}00`); band.addColorStop(.5, `${theme.accent}12`); band.addColorStop(1, `${theme.accent}00`);
  ctx.fillStyle = band; ctx.fillRect(0, 0, w, h); ctx.restore();
}

// Vector stamp remains sharp at the full export resolution in every format.
function originalBadge(ctx,x,y,theme){
  ctx.save();round(ctx,x,y,70,26,6);ctx.fillStyle=`${theme.accent}16`;ctx.fill();ctx.strokeStyle=`${theme.accent}77`;ctx.lineWidth=1;ctx.stroke();
  ctx.beginPath();ctx.moveTo(x+16,y+4);ctx.lineTo(x+24,y+9);ctx.lineTo(x+24,y+17);ctx.lineTo(x+16,y+22);ctx.lineTo(x+8,y+17);ctx.lineTo(x+8,y+9);ctx.closePath();ctx.stroke();
  ctx.beginPath();ctx.moveTo(x+11,y+10);ctx.lineTo(x+20,y+10);ctx.moveTo(x+15,y+10);ctx.lineTo(x+15,y+17);ctx.moveTo(x+18,y+13);ctx.lineTo(x+18,y+17);ctx.lineTo(x+21,y+17);ctx.stroke();
  text(ctx,'OG',x+33,y+18,13,theme.accent,theme.font,700);ctx.restore();
}

function profileArtwork(ctx, { model, theme, avatar, profileQR, sampleProfile, ink, muted, color, w, h, pad, wide, story, time, foilTime, animateHero, layout, boxed, pixelScale }) {
  const accent = model.owner ? theme.gold || '#d5b46a' : theme.accent;
  premiumFrame(ctx, w, h, pad, { ...theme, accent }, foilTime);
  const headerY = story ? 232 : pad + 34;
  wordmark(ctx, pad, headerY, 36, theme.light && ink !== '#ffffff');
  const edition = sampleProfile ? 'EXAMPLE / WORKSPACE' : model.owner ? 'OWNER EDITION' : 'PUBLIC JOURNAL';
  ctx.font = `600 13px ${theme.font}`;
  const badgeW = ctx.measureText(edition).width + 44, badgeX = w - pad - badgeW;
  round(ctx, badgeX, headerY - 26, badgeW, 36, 18); ctx.fillStyle = `${accent}18`; ctx.fill(); ctx.strokeStyle = `${accent}66`; ctx.lineWidth = 1; ctx.stroke();
  ctx.beginPath(); ctx.arc(badgeX + 17, headerY - 8, 3, 0, Math.PI * 2); ctx.fillStyle = accent; ctx.fill();
  text(ctx, edition, badgeX + 30, headerY - 3, 13, accent, theme.font, 600);

  const avatarSize = wide ? 94 : story ? 152 : 128, identityY = wide ? 164 : story ? 400 : 190, nameX = pad + avatarSize + 30;
  const halo = ctx.createRadialGradient(pad + avatarSize / 2, identityY + avatarSize / 2, avatarSize / 2, pad + avatarSize / 2, identityY + avatarSize / 2, avatarSize);
  halo.addColorStop(0, `${accent}2e`); halo.addColorStop(1, `${accent}00`); ctx.fillStyle = halo; ctx.fillRect(pad - avatarSize / 2, identityY - avatarSize / 2, avatarSize * 2, avatarSize * 2);
  ctx.save(); round(ctx, pad, identityY, avatarSize, avatarSize, avatarSize / 2); ctx.fillStyle = `${theme.accent}25`; ctx.fill(); ctx.clip();
  if (avatar) containedImage(ctx, avatar, pad, identityY, avatarSize, avatarSize);
  else { ctx.textAlign = 'center'; text(ctx, model.name.slice(0, 1).toUpperCase(), pad + avatarSize / 2, identityY + avatarSize * .69, avatarSize * .5, ink, theme.heading, 600); }
  ctx.restore();
  ctx.strokeStyle = `${accent}aa`; ctx.lineWidth = 2; round(ctx, pad - 5, identityY - 5, avatarSize + 10, avatarSize + 10, (avatarSize + 10) / 2); ctx.stroke();
  text(ctx, model.name, nameX, identityY + avatarSize * .6, wide ? 54 : 76, ink, theme.heading, 600, wide ? 610 : w - nameX - pad);
  text(ctx, model.owner ? 'TRADELOG OWNER' : 'TRADER / PUBLIC PROFILE', nameX, identityY + avatarSize * .91, wide ? 13 : 16, model.owner ? accent : muted, theme.font, 500, wide ? 610 : w - nameX - pad);

  const heroY = wide ? 377 : h * .445, statsWidth = wide ? 660 : 670;
  if (model.statsHidden) {
    text(ctx, 'A journal.', pad, heroY - 15, wide ? 62 : 84, ink, theme.heading, 500);
    text(ctx, 'A journey.', pad, heroY + (wide ? 54 : 82), wide ? 62 : 84, ink, theme.heading, 500);
    text(ctx, 'Performance kept private.', pad, heroY + (wide ? 100 : 136), 17, muted, theme.font, 400);
  } else {
    text(ctx, 'PUBLIC-TRADE RETURN', pad, heroY - (wide ? 82 : 104), wide ? 13 : 16, muted, theme.font, 500);
    const labels = animateHero ? [model.hero, ...Array.from({length:10}, (_, digit) => model.hero.replace(/\d/g, String(digit)))] : [model.hero];
    let heroSize = wide ? 96 : 132; ctx.font = `700 ${heroSize}px ${theme.heading}`;
    while (heroSize > 34 && labels.some(label => ctx.measureText(label).width > statsWidth)) { heroSize -= 2; ctx.font = `700 ${heroSize}px ${theme.heading}`; }
    heroText(ctx, animateHero ? countedHero(model.hero, time) : model.hero, pad, heroY, heroSize, color, theme.heading, statsWidth, theme.light, color, pixelScale);
    if (layout !== 'result') model.metrics.forEach(([label, value], index) => {
      const x = pad + index % 2 * (statsWidth / 2 + 10), y = heroY + 46 + Math.floor(index / 2) * 94;
      if (boxed) { round(ctx, x, y - 12, statsWidth / 2 - 10, 82, 12); ctx.fillStyle = `${theme.s2}aa`; ctx.fill(); }
      text(ctx, label.toUpperCase(), x + (boxed ? 14 : 0), y + 8, wide ? 12 : 15, muted, theme.font, 500);
      text(ctx, value, x + (boxed ? 14 : 0), y + 43, wide ? 28 : 36, ink, theme.heading, 600, statsWidth / 2 - 30);
    });
    if (layout === 'chart' && model.points.length > 1) {
      const y = wide ? 527 : h * (story ? .7 : .76), cw = wide ? statsWidth : w - pad * 2, ch = wide ? 50 : h * (story ? .135 : .1);
      text(ctx, 'PUBLIC HISTORY', pad, y - 20, wide ? 11 : 15, muted, theme.font, 500);
      equity(ctx, model.points, pad + 3, y, cw - 8, ch, color, { ...theme, t1: ink });
      if (!wide) text(ctx, 'Sum of recorded returns · not compounded', pad, y + ch + 29, 13, muted, theme.font, 400, cw);
    }
  }

  // A stable, high-contrast scan area stays independent of the moving foil and
  // uploaded backgrounds. Its four-module quiet zone survives every format.
  const panelX = w - pad - 312, panelY = wide ? 166 : h * (story ? .36 : .34), panelH = 432;
  round(ctx, panelX, panelY, 312, panelH, 22); ctx.fillStyle = ink === '#ffffff' && theme.light ? '#08110fe8' : `${theme.s1}e8`; ctx.fill(); ctx.strokeStyle = `${accent}66`; ctx.lineWidth = 1.5; ctx.stroke();
  text(ctx, sampleProfile ? 'EXPLORE THE WORKSPACE' : 'SCAN TO EXPLORE', panelX + 26, panelY + 31, 12, accent, theme.font, 600, 264);
  if (profileQR) {
    ctx.save(); round(ctx, panelX + 26, panelY + 52, 260, 260, 13); ctx.clip(); drawProfileQR(ctx, profileQR, panelX + 26, panelY + 52, 260, pixelScale); ctx.restore();
    text(ctx, sampleProfile ? 'Explore TradeLog' : `@${model.profileLink.split('/').at(-1)}`, panelX + 26, panelY + 351, 24, ink, theme.heading, 600, 264);
    text(ctx, 'trade-log.co.uk', panelX + 26, panelY + 381, 15, muted, theme.font, 400, 264);
    text(ctx, sampleProfile ? 'EXAMPLE RECORDS' : 'YOUR PUBLIC JOURNAL', panelX + 26, panelY + 412, 11, accent, theme.font, 500);
  } else {
    text(ctx, 'Your public', panelX + 26, panelY + 135, 39, ink, theme.heading, 500, 260);
    text(ctx, 'journal.', panelX + 26, panelY + 183, 39, ink, theme.heading, 500, 260);
    text(ctx, 'Add a public nickname', panelX + 26, panelY + 282, 17, muted, theme.font, 400, 260);
    text(ctx, 'to include your QR code.', panelX + 26, panelY + 310, 17, muted, theme.font, 400, 260);
  }
  const footer = h - (story ? 210 : 45);
  ctx.strokeStyle = `${ink}24`; ctx.lineWidth = 1; ctx.beginPath(); ctx.moveTo(pad, footer - 27); ctx.lineTo(w - pad, footer - 27); ctx.stroke();
  if(model.originalMember)originalBadge(ctx,pad,footer-20,theme);
  text(ctx, sampleProfile ? 'SAMPLE RECORDS / PREVIEW' : model.statsHidden ? 'PERFORMANCE KEPT PRIVATE' : 'PUBLIC RECORDS ONLY', pad+(model.originalMember?86:0), footer, wide ? 11 : 14, muted, theme.font, 500);
  ctx.textAlign = 'right'; text(ctx, model.owner ? 'L / TRADELOG OWNER' : 'TRADELOG / JOURNAL', w - pad, footer, wide ? 11 : 14, model.owner ? accent : muted, theme.font, 500); ctx.textAlign = 'left';
}

// One renderer owns both the live preview and every exported frame. It avoids
// screenshotting a moving DOM tree and keeps uploaded media local to the device.
export function drawComposerFrame(canvas, { model, theme, design: inputDesign, background = 'aurora', media, gifAnimation, chart, avatar, profileQR, sampleProfile = false, position, zoom = 1, dim = 45, layout = 'essentials', boxed = false, corners = 'rounded', time = 0, opaque = false, videoBackground = false, animateHero = false, layer = 'all' }) {
  const design = normaliseComposerDesign(inputDesign);
  theme = { ...theme, ...composerFonts(design, theme) };
  if (gifAnimation) media = gifAnimation.frameAt(time);
  if (media?.tagName === 'VIDEO' && media.readyState < 2) media = null;
  const ctx = canvas.getContext('2d'); if (!ctx) return;
  const scale = canvas.width / 1200, w = 1200, h = canvas.height / scale, wide = h < 900, story = h > 1700;
  const chartLead = wide && layout === 'chart' && model.kind === 'trade';
  const pad = wide ? 64 : 76;
  ctx.save(); ctx.scale(scale, scale); ctx.clearRect(0, 0, w, h); ctx.imageSmoothingEnabled = true; ctx.imageSmoothingQuality = 'high';
  if (opaque && layer !== 'foreground') { ctx.fillStyle = theme.bg; ctx.fillRect(0, 0, w, h); }
  round(ctx, 0, 0, w, h, corners === 'rounded' ? 32 : 0); ctx.clip();
  // Video covers the whole frame. Avoid painting costly animated gradients
  // underneath it, especially while recording a 4K canvas on mobile devices.
  if (layer !== 'foreground') {
    if (media?.tagName === 'VIDEO' || videoBackground) { ctx.fillStyle = theme.bg; ctx.fillRect(0, 0, w, h); }
    else procedural(ctx, w, h, theme, background, time);
  }
  const mediaWidth = media?.videoWidth || media?.naturalWidth || media?.width, mediaHeight = media?.videoHeight || media?.naturalHeight || media?.height;
  if (layer !== 'foreground' && mediaWidth && mediaHeight) {
    const place = coverPlacement(mediaWidth, mediaHeight, w, h, position, zoom);
    ctx.drawImage(media, place.x, place.y, place.width, place.height);
    ctx.fillStyle = `rgba(0,0,0,${clamp(dim, 0, 85) / 100})`; ctx.fillRect(0, 0, w, h);
    const shade = ctx.createLinearGradient(0, 0, 0, h); shade.addColorStop(0, '#00000022'); shade.addColorStop(.6, '#00000000'); shade.addColorStop(1, '#00000088'); ctx.fillStyle = shade; ctx.fillRect(0, 0, w, h);
  }
  if (layer === 'background') { ctx.restore(); return; }
  const ink = media ? '#ffffff' : theme.t1, muted = media ? '#ffffffb3' : theme.t3;
  const customColour = design[model.neutral ? 'neutralColour' : model.negative ? 'negativeColour' : 'positiveColour'];
  const color = customColour || (model.neutral ? theme.neutral : model.negative ? theme.negative : theme.positive);
  const safeW = w - pad * 2;
  if (model.kind === 'profile') {
    profileArtwork(ctx, { model, theme, avatar, profileQR, sampleProfile, ink, muted, color, w, h, pad, wide, story, time, foilTime: background === 'plain' ? 0 : time, animateHero, layout, boxed, pixelScale: scale });
    drawCaption(ctx, model, design, theme, ink, w, h, pad, story, scale, layout);
    ctx.restore(); return;
  }
  // Compose the identity, result and supporting figures around one shared
  // anchor. Moving the block alone leaves centre/right cards looking lopsided.
  const statsWidth = chartLead ? 310 : wide ? 440 : 520;
  const canPosition = model.kind === 'trade' && layout !== 'chart';
  const contentAlign = canPosition ? design.contentAlign : 'left';
  const alignment = { left: 0, center: .5, right: 1 }[contentAlign];
  const contentX = pad + (safeW - statsWidth) * alignment;
  const contentAnchor = contentX + statsWidth * alignment;
  const titleSize = wide ? 29 : model.kind === 'profile' ? 54 : 39;
  const baseHeroY = wide ? 265 : story ? 575 : 430;
  const count = model.metrics.length, columns = Math.min(2, Math.max(1, count));
  const contentBottom = baseHeroY + (layout === 'result' || !count ? 10 : 104 + (Math.ceil(count / columns) - 1) * 104);
  const roomBelow = Math.max(0, h - (story ? 260 : 58) - (model.caption ? 180 : 84) - contentBottom);
  const contentY = canPosition ? roomBelow * ({ top: 0, middle: .5, bottom: 1 }[design.contentHeight]) : 0;
  const headerY = pad + (story ? 130 : 0) + contentY;
  ctx.save(); ctx.textAlign = contentAlign;
  text(ctx, model.title, contentAnchor, headerY + titleSize, titleSize, ink, theme.heading, 650, statsWidth);
  text(ctx, model.subtitle, contentAnchor, headerY + titleSize + 33, 17, muted, theme.font, 500, statsWidth);
  const heroY = baseHeroY + contentY;
  text(ctx, model.heroLabel, contentAnchor, heroY - (wide ? 91 : 117), 14, muted, theme.font, 500, statsWidth);
  let heroSize = (wide ? 100 : 125) * design.resultScale / 100;
  const hero = animateHero ? countedHero(model.hero, time) : model.hero;
  const heroW = statsWidth;
  // Reserve the widest digits before counting. Proportional fonts can make
  // 0.00 wider than the final result, which otherwise clips the animation.
  const heroLabels = animateHero ? [model.hero, ...Array.from({length:10}, (_, digit) => model.hero.replace(/\d/g, String(digit)))] : [model.hero];
  ctx.font = `700 ${heroSize}px ${theme.heading}`;
  while (heroSize > 34 && heroLabels.some(label => ctx.measureText(label).width > heroW)) { heroSize -= 2; ctx.font = `700 ${heroSize}px ${theme.heading}`; }
  heroText(ctx, hero, contentAnchor, heroY, heroSize, customColour || (media ? '#ffffff' : color), theme.heading, heroW, theme.light && !media, color, scale);
  const metricY = heroY + 58;
  const metricWidth = (statsWidth - (columns - 1) * 20) / columns;
  if (layout !== 'result') model.metrics.forEach(([label, value], index) => {
    const row = Math.floor(index / columns), rowCount = Math.min(columns, count - row * columns);
    const rowWidth = rowCount * metricWidth + (rowCount - 1) * 20;
    const x = contentX + (statsWidth - rowWidth) * alignment + (index % columns) * (metricWidth + 20), y = metricY + row * 104;
    const inset = boxed ? 16 : 0;
    const anchor = x + inset + (metricWidth - inset * 2) * alignment;
    if (boxed) { round(ctx, x, y - 13, metricWidth, 86, 13); ctx.fillStyle = media ? '#ffffff16' : `${theme.s2}cc`; ctx.fill(); }
    text(ctx, label, anchor, y + 10, 15, muted, theme.font, 400, metricWidth - inset * 2);
    text(ctx, value, anchor, y + 46, chartLead && count > 2 ? 23 : 26, ink, theme.heading, 550, metricWidth - inset * 2);
  });
  ctx.restore(); ctx.textAlign = 'left';
  if (layout === 'chart') {
    const x = chartLead ? 420 : wide ? 635 : pad, y = chartLead ? 105 : wide ? 180 : metricY + (count ? Math.ceil(count / columns) * 104 + 26 : 60);
    const cw = chartLead ? 716 : wide ? 495 : safeW, ch = chartLead ? (model.caption ? 365 : 435) : wide ? (model.caption ? 240 : 310) : Math.min(story ? 660 : 350, h - y - (story ? 260 : 58) - (model.caption ? 220 : 140));
    if (ch > 30) {
      if (chart) { round(ctx, x, y, cw, ch, 18); ctx.fillStyle = media ? '#00000045' : `${theme.s1}bb`; ctx.fill(); containedImage(ctx, chart, x + 12, y + 12, cw - 24, ch - 24); }
      else if (model.months?.length) monthlyBars(ctx, model, wide ? 535 : x, wide ? 157 : y, wide ? 601 : cw, wide ? (model.caption ? 297 : 367) : Math.max(290, ch), { ...theme, ink, muted }, time, animateHero);
      else if (model.kind !== 'trade') { text(ctx, model.chartLabel || 'RECORDED EQUITY', x, y + 12, 14, muted, theme.font, 500); equity(ctx, model.points, x + 4, y + 44, cw - 12, ch - 90, color, { ...theme, t1: ink }); text(ctx, model.chartNote || '', x, y + ch, 12, muted, theme.font, 400, cw); }
    }
  }
  drawCaption(ctx, model, design, theme, ink, w, h, pad, story, scale, layout);
  const footer = h - (story ? 260 : 58);
  ctx.strokeStyle = media ? '#ffffff33' : theme.s3; ctx.lineWidth = 1; ctx.beginPath(); ctx.moveTo(pad, footer - 45); ctx.lineTo(w - pad, footer - 45); ctx.stroke();
  wordmark(ctx, pad, footer, 30, !media && theme.light);
  const brandEnd = pad + 172, avatarX = brandEnd + 23;
  if (avatar) { ctx.save(); round(ctx, avatarX, footer - 27, 32, 32, 16); ctx.clip(); containedImage(ctx, avatar, avatarX, footer - 27, 32, 32); ctx.restore(); }
  text(ctx, model.name, avatarX + (avatar ? 43 : 0), footer - 2, 18, ink, theme.font, 600, wide ? 300 : 480);
  if (model.owner) text(ctx, 'OWNER', avatarX + (avatar ? 43 : 0), footer + 20, 10, theme.gold, theme.font, 600);
  const site = 'trade-log.co.uk'; ctx.font = `400 13px ${theme.font}`;
  if(model.originalMember)originalBadge(ctx,w-pad-ctx.measureText(site).width-86,footer-22,theme);
  text(ctx, site, w - pad - ctx.measureText(site).width, footer - 2, 13, muted, theme.font, 400);
  ctx.restore();
}
